import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { getPageVariablesBatch } from '@/services/api/connections';
import { validateBroadcastVariables } from '@/services/api/broadcast';
import { BroadcastFormData } from '../types/broadcast.types';
import type { Page, ValidateVariablesResponse } from '@/types/broadcast';
import type { ContentBlock } from '@/types/flow';
import type { PageVariable } from '@/pages/Meta/Connection/types/meta.types';
import StepSelectPage from './StepSelectPage';
import StepConfigureBlocks from './StepConfigureBlocks';
import StepSchedule from './StepSchedule';
import StepReview from './StepReview';
import MessengerPreview from './MessengerPreview';
import VariablesCopyDialog from './VariablesCopyDialog';
import { UtilityTemplatePicker } from './UtilityTemplatePicker';
import type { BroadcastUtilityTemplate } from '@/services/api/broadcast';
import { ChevronLeft, ChevronRight, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';

const DEFAULT_FORM_DATA: BroadcastFormData = {
    selectedPages: [],
    blocks: [],
    isScheduled: false,
    scheduledTime: '',
    // Default = utility (fora da janela de 24h) — caso mais comum no uso real.
    broadcastType: 'utility',
    messageTag: 'ACCOUNT_UPDATE',
};

interface CreateBroadcastModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSubmit: (data: BroadcastFormData) => void;
    isLoading: boolean;
    initialData?: BroadcastFormData;
    isEditMode?: boolean;
}

export default function CreateBroadcastModal({
                                                 open,
                                                 onOpenChange,
                                                 onSubmit,
                                                 isLoading,
                                                 initialData,
                                                 isEditMode = false,
                                             }: CreateBroadcastModalProps) {
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState<BroadcastFormData>(DEFAULT_FORM_DATA);
    const [validating, setValidating] = useState(false);
    const [validationResult, setValidationResult] = useState<ValidateVariablesResponse | null>(null);
    const [showCopyDialog, setShowCopyDialog] = useState(false);
    // UTILITY template (opt-in): id mora em formData, objeto fica em cache só pra exibir chip
    const [selectedUtilityTemplate, setSelectedUtilityTemplate] = useState<BroadcastUtilityTemplate | null>(null);
    const handleSelectUtilityTemplate = (t: BroadcastUtilityTemplate | null) => {
        setSelectedUtilityTemplate(t);
        setFormData((prev) => ({ ...prev, customTemplateId: t?.id ?? null }));
    };

    // Buscar variáveis das páginas selecionadas via batch (1 request)
    const pageIds = formData.selectedPages.map(p => p.id).filter((id): id is number => typeof id === 'number');
    const { data: batchVariables } = useQuery({
        queryKey: ['page-variables-batch', pageIds],
        queryFn: () => getPageVariablesBatch(pageIds),
        enabled: pageIds.length > 0,
        staleTime: 1000 * 60 * 2,
    });
    const pageVariables = useMemo((): PageVariable[] => {
        if (!batchVariables) return [];
        const seen = new Map<string, PageVariable>();
        for (const vars of Object.values(batchVariables)) {
            for (const v of vars) {
                if (!seen.has(v.key)) seen.set(v.key, v);
            }
        }
        return Array.from(seen.values());
    }, [batchVariables]);

    useEffect(() => {
        if (open) {
            setFormData(initialData || DEFAULT_FORM_DATA);
            setCurrentStep(1);
            setValidationResult(null);
            // Limpa state local — hidratação acontece no useEffect abaixo se initialData trouxer customTemplateId
            setSelectedUtilityTemplate(null);
        }
    }, [open, initialData]);

    // Hidrata selectedUtilityTemplate quando duplicar broadcast trouxer customTemplateId.
    // Busca o template via mesma query do picker (cache compartilhado).
    useEffect(() => {
        if (!open) return;
        const tid = formData.customTemplateId;
        const internalPageIds = formData.selectedPages.map(p => p.id).filter((id): id is number => typeof id === 'number');
        if (!tid || !internalPageIds.length) return;
        if (selectedUtilityTemplate?.id === tid) return;
        import('@/services/api/broadcast').then(({ getBroadcastUtilityTemplates }) => {
            getBroadcastUtilityTemplates(internalPageIds).then(resp => {
                const t = resp.templates.find(x => x.id === tid);
                if (t) setSelectedUtilityTemplate(t);
            }).catch(() => { /* silent — picker mostra opção de selecionar manualmente */ });
        });
    }, [open, formData.customTemplateId, formData.selectedPages, selectedUtilityTemplate?.id]);

    const steps = [
        { number: 1, title: 'Páginas', description: 'Escolha as páginas' },
        { number: 2, title: 'Conteúdo', description: 'Monte a mensagem' },
        { number: 3, title: 'Agendar', description: 'Escolha quando enviar' },
        { number: 4, title: 'Revisar', description: 'Confirme os detalhes' }
    ];

    const totalSteps = 4;

    const handleNext = async () => {
        if (currentStep === 2 && formData.selectedPages.length > 0 && formData.blocks.length > 0) {
            setValidating(true);
            try {
                const result = await validateBroadcastVariables({
                    page_ids: formData.selectedPages.map(p => p.id).filter((id): id is number => typeof id === 'number'),
                    blocks: formData.blocks,
                });
                setValidationResult(result);
                // Se há variáveis faltantes, abre o modal e NÃO avança
                if (result && !result.all_resolved) {
                    setShowCopyDialog(true);
                    setValidating(false);
                    return;
                }
            } catch {
                setValidationResult(null);
            } finally {
                setValidating(false);
            }
        }
        if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
    };

    const handlePrev = () => {
        if (currentStep > 1) setCurrentStep(currentStep - 1);
    };

    const handleSubmit = () => {
        if (isLoading) return;
        onSubmit(formData);
    };

    const handleTogglePage = (page: Page) => {
        setFormData(prev => {
            const exists = prev.selectedPages.some(p => p.page_id === page.page_id);
            const selectedPages = exists
                ? prev.selectedPages.filter(p => p.page_id !== page.page_id)
                : [...prev.selectedPages, page];
            return { ...prev, selectedPages };
        });
    };

    const hasBlockContent = (block: ContentBlock): boolean => {
        if (block.type === 'text') return !!block.message?.trim();
        return !!(block.media_url || block.attachment_id);
    };

    const canProceed = (): boolean => {
        switch (currentStep) {
            case 1:
                return formData.selectedPages.length > 0;
            case 2:
                // Template UTILITY selecionado já tem conteúdo aprovado — não exige blocks.
                // Checa formData (source of truth) e state local — ambos têm que coincidir.
                if (selectedUtilityTemplate && formData.customTemplateId === selectedUtilityTemplate.id) return true;
                return formData.blocks.length > 0 && formData.blocks.some(hasBlockContent);
            case 3:
                if (formData.isScheduled) return !!formData.scheduledTime;
                return true;
            case 4:
                // Bloqueia envio se há variáveis faltantes
                if (validationResult && !validationResult.all_resolved) return false;
                return true;
            default:
                return false;
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <StepSelectPage
                        selectedPages={formData.selectedPages}
                        onTogglePage={handleTogglePage}
                        onSetPages={(pages) => setFormData(prev => ({ ...prev, selectedPages: pages }))}
                    />
                );
            case 2:
                return (
                    <div className="space-y-4">
                        <UtilityTemplatePicker
                            pageIds={pageIds}
                            pageFbIds={formData.selectedPages.map((p) => String(p.page_id)).filter(Boolean)}
                            selectedTemplateId={selectedUtilityTemplate?.id ?? null}
                            onSelect={handleSelectUtilityTemplate}
                            onUseAndAdvance={(t) => {
                                handleSelectUtilityTemplate(t);
                                setCurrentStep(3);
                            }}
                        />
                        <StepConfigureBlocks
                            blocks={formData.blocks}
                            onChange={(blocks) => setFormData(prev => ({ ...prev, blocks }))}
                            pageId={formData.selectedPages[0]?.page_id}
                            pageVariables={pageVariables}
                        />
                    </div>
                );
            case 3:
                return (
                    <StepSchedule
                        isScheduled={formData.isScheduled}
                        scheduledTime={formData.scheduledTime}
                        broadcastType={formData.broadcastType || 'utility'}
                        messageTag={formData.messageTag}
                        onIsScheduledChange={(value) => setFormData({ ...formData, isScheduled: value })}
                        onScheduledTimeChange={(value) => setFormData({ ...formData, scheduledTime: value })}
                        onBroadcastTypeChange={(type, tag) => setFormData({ ...formData, broadcastType: type, messageTag: tag })}
                    />
                );
            case 4:
                return (
                    <StepReview
                        formData={formData}
                        onChange={(aiTranslate, targetLanguage, aiProvider) =>
                            setFormData(prev => ({ ...prev, aiTranslate, targetLanguage, aiProvider }))
                        }
                        onIncludeInactiveChange={(value) =>
                            setFormData(prev => ({ ...prev, includeInactive: value }))
                        }
                        onAudienceWindowChange={(value) =>
                            setFormData(prev => ({ ...prev, audienceWindow: value }))
                        }
                    />
                );
            default:
                return null;
        }
    };

    const previewPageLabel = (() => {
        if (formData.selectedPages.length === 0) return null;
        if (formData.selectedPages.length === 1) return formData.selectedPages[0].page_name;
        return `${formData.selectedPages[0].page_name} e mais ${formData.selectedPages.length - 1}`;
    })();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 overflow-hidden">
                <div className="flex h-[95vh]">
                    {/* Left Panel - Steps */}
                    <div className="w-2/3 flex flex-col bg-gray-50 border-r border-gray-200">
                        {/* Header com Progress */}
                        <div className="bg-white border-b border-gray-200 p-6">
                            <h2 className="text-2xl font-bold text-gray-900 mb-3">
                                {isEditMode ? 'Editar Broadcast' : 'Criar Novo Broadcast'}
                            </h2>
                            <Input
                                placeholder="Nome do broadcast (ex: Campanha Black Friday)"
                                value={formData.title || ''}
                                onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                className="mb-4 text-sm"
                            />

                            {/* Progress Steps */}
                            <div className="flex items-center justify-between">
                                {steps.map((step, index) => {
                                    const isActive = currentStep === step.number;
                                    const isCompleted = currentStep > step.number;

                                    return (
                                        <div key={step.number} className="flex items-center flex-1">
                                            <div className="flex flex-col items-center flex-1">
                                                <div
                                                    className={`
                                                        w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm
                                                        ${isCompleted
                                                        ? 'bg-green-500 text-white'
                                                        : isActive
                                                            ? 'bg-[#7dd3fc] text-white'
                                                            : 'bg-gray-200 text-gray-500'
                                                    }
                                                    `}
                                                >
                                                    {isCompleted ? <Check className="h-5 w-5" /> : step.number}
                                                </div>
                                                <p className={`text-xs mt-2 font-medium ${isActive ? 'text-[#0f172a]' : 'text-gray-500'}`}>
                                                    {step.title}
                                                </p>
                                            </div>
                                            {index < steps.length - 1 && (
                                                <div
                                                    className={`h-0.5 flex-1 ${
                                                        currentStep > step.number ? 'bg-green-500' : 'bg-gray-200'
                                                    }`}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Step Content */}
                        <div className="flex-1 overflow-y-auto p-8">
                            {renderStep()}
                        </div>

                        {/* Footer com Botões */}
                        <div className="bg-white border-t border-gray-200 p-6 flex justify-between">
                            <Button
                                variant="outline"
                                onClick={handlePrev}
                                disabled={currentStep === 1 || isLoading}
                            >
                                <ChevronLeft className="mr-2 h-4 w-4" />
                                Anterior
                            </Button>

                            {currentStep < totalSteps ? (
                                <Button
                                    onClick={handleNext}
                                    disabled={!canProceed() || isLoading || validating}
                                    className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                                >
                                    {validating ? (
                                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validando...</>
                                    ) : (
                                        <>Próximo<ChevronRight className="ml-2 h-4 w-4" /></>
                                    )}
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleSubmit}
                                    disabled={!canProceed() || isLoading}
                                    className="bg-green-600 hover:bg-green-700"
                                >
                                    {isLoading
                                        ? (isEditMode ? 'Salvando...' : 'Enviando...')
                                        : (isEditMode ? 'Salvar Alterações' : 'Confirmar e Enviar')
                                    }
                                    <Check className="ml-2 h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Right Panel - Preview */}
                    <div className="w-1/3 bg-white overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
                            <h3 className="font-semibold text-gray-900 text-center">Preview em Tempo Real</h3>
                            {previewPageLabel && (
                                <div className="mt-3 flex items-center gap-2 justify-center text-sm text-gray-600">
                                    <div className="w-6 h-6 bg-[#0f172a] rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                        {formData.selectedPages[0].page_name.substring(0, 2).toUpperCase()}
                                    </div>
                                    <span>{previewPageLabel}</span>
                                </div>
                            )}
                        </div>

                        {/* Variable validation warning */}
                        {validationResult && !validationResult.all_resolved && currentStep >= 3 && (
                            <div className="mx-4 mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                                    <p className="text-sm font-semibold text-amber-800">Variáveis faltando</p>
                                </div>
                                <div className="space-y-1 mb-3">
                                    {validationResult.pages.filter(p => !p.can_resolve).map(p => (
                                        <div key={p.id} className="text-xs text-amber-700">
                                            <span className="font-medium">{p.page_name}:</span>{' '}
                                            {p.variables_missing.join(', ')}
                                        </div>
                                    ))}
                                </div>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full border-amber-400 text-amber-800 hover:bg-amber-100"
                                    onClick={() => setShowCopyDialog(true)}
                                >
                                    Corrigir variáveis
                                </Button>
                            </div>
                        )}

                        {validationResult?.all_resolved && currentStep >= 3 && (
                            <div className="mx-4 mt-4 rounded-lg border border-green-200 bg-green-50 p-3 flex items-center gap-2">
                                <Check className="h-4 w-4 text-green-600 shrink-0" />
                                <p className="text-xs text-green-700 font-medium">Todas as variáveis resolvidas</p>
                            </div>
                        )}

                        <div className="p-6">
                            <MessengerPreview
                                blocks={formData.blocks}
                                pageName={formData.selectedPages[0]?.page_name}
                                utilityTemplate={selectedUtilityTemplate}
                            />
                        </div>
                    </div>
                </div>
            </DialogContent>

            {showCopyDialog && validationResult && (
                <VariablesCopyDialog
                    open={showCopyDialog}
                    onOpenChange={setShowCopyDialog}
                    pages={validationResult.pages}
                    selectedPages={formData.selectedPages}
                    onFixed={async () => {
                        // Re-run validation after fix
                        try {
                            const result = await validateBroadcastVariables({
                                page_ids: formData.selectedPages.map(p => p.id).filter((id): id is number => typeof id === 'number'),
                                blocks: formData.blocks,
                            });
                            setValidationResult(result);
                        } catch {
                            // ignore
                        }
                    }}
                />
            )}
        </Dialog>
    );
}
